/**
 * Espace Famille — notifications push (#2542).
 * Chargé sous /parent via layouts/pwa-head.blade.php.
 *
 * ⚠ L'encart vit dans #app, sur lequel Vue est monté : Vue remplace ce DOM à
 * son rendu. D'où la visibilité portée par une classe sur <html> plutôt que
 * par un attribut, et les clics délégués au document plutôt qu'aux éléments —
 * même parti pris que le bandeau d'installation de #2541.
 *
 * Le SDK Firebase n'est pas empaqueté : il est chargé depuis gstatic, et
 * seulement quand il sert. Une PWA parentale sur mobile n'a pas à payer ce
 * poids au chargement de chaque page.
 */

import { meta, reportAppState } from './parent-app-state';

const ENABLED_KEY = 'moser-famille-push-enabled';
const SDK_VERSION = '12.13.0';
const SDK_BASE = 'https://www.gstatic.com/firebasejs/' + SDK_VERSION + '/';

const OFFER_CLASS = 'push-can-enable';
const ENABLED_CLASS = 'push-enabled';
const BLOCKED_CLASS = 'push-blocked';

// Relègue l'encart en pied de page. Posée à l'ouverture de la page dès que le
// parent a tranché, mais volontairement pas juste après un clic sur « Activer » :
// la confirmation mérite d'être vue là où le geste a eu lieu.
const FOOTER_SLOT_CLASS = 'push-slot-footer';

let sdkPromise = null;

function config() {
    try {
        return JSON.parse(meta('push-firebase-config') || '{}');
    } catch (e) {
        return {};
    }
}

// Sans projet Firebase configuré, l'encart n'a rien à proposer.
function isConfigured() {
    const values = config();

    return Boolean(values.apiKey && values.projectId && values.messagingSenderId && values.appId)
        && Boolean(meta('push-vapid-key'));
}

function isIos() {
    const ua = window.navigator.userAgent;

    return /iPad|iPhone|iPod/.test(ua)
        || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
}

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

/**
 * iOS n'accepte le push que depuis l'écran d'accueil, application installée et
 * lancée en mode standalone. Un parent iPhone qui consulte l'Espace Famille
 * dans un onglet Safari ne recevra jamais rien : lui proposer d'activer les
 * notifications serait une promesse en l'air. Le bandeau d'installation de
 * #2541 prend le relais.
 */
function isSupported() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
        return false;
    }

    return !isIos() || isStandalone();
}

function read(key) {
    try {
        return window.localStorage.getItem(key) === '1';
    } catch (e) {
        return false;
    }
}

function write(key, value) {
    try {
        if (value) {
            window.localStorage.setItem(key, '1');
        } else {
            window.localStorage.removeItem(key);
        }
    } catch (e) {
        // Sans stockage, l'état ne tient que le temps de la session.
    }
}

function demote() {
    document.documentElement.classList.add(FOOTER_SLOT_CLASS);
}

function show(variantClass) {
    document.documentElement.classList.remove(OFFER_CLASS, ENABLED_CLASS, BLOCKED_CLASS);

    if (variantClass) {
        document.documentElement.classList.add(variantClass);
    }
}

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('Chargement impossible : ' + src));

        document.head.appendChild(script);
    });
}

function loadSdk() {
    if (!sdkPromise) {
        // Les deux scripts sont enchaînés : `messaging-compat` suppose que
        // `app-compat` a déjà posé le global `firebase`.
        sdkPromise = loadScript(SDK_BASE + 'firebase-app-compat.js')
            .then(() => loadScript(SDK_BASE + 'firebase-messaging-compat.js'));
    }

    return sdkPromise;
}

async function messaging() {
    await loadSdk();

    // Renvoie une promesse depuis la v9 du SDK, un booléen avant.
    const supported = await Promise.resolve(firebase.messaging.isSupported());

    if (!supported) {
        return null;
    }

    if (!firebase.apps.length) {
        firebase.initializeApp(config());
    }

    return firebase.messaging();
}

async function currentToken() {
    const instance = await messaging();

    if (!instance) {
        console.error('[push] Firebase Messaging non supporté par ce navigateur.');

        return null;
    }

    const registration = await navigator.serviceWorker.register(
        meta('push-service-worker'),
        { scope: meta('push-scope') }
    );

    return instance.getToken({
        vapidKey: meta('push-vapid-key'),
        serviceWorkerRegistration: registration,
    });
}

function sendToken(method, url, token) {
    return fetch(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': meta('csrf-token'),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ token }),
    });
}

async function enable() {
    // La permission doit partir d'un geste du parent : demandée au chargement,
    // elle serait refusée par la plupart des navigateurs, et ce refus vaudrait
    // définitivement pour l'origine.
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
        show(permission === 'denied' ? BLOCKED_CLASS : OFFER_CLASS);

        return;
    }

    try {
        const token = await currentToken();

        if (!token) {
            console.error('[push] Firebase n\'a rendu aucun jeton.');
            show(OFFER_CLASS);

            return;
        }

        await sendToken('POST', meta('push-store-url'), token);

        write(ENABLED_KEY, true);
        show(ENABLED_CLASS);
    } catch (e) {
        console.error('[push] Activation impossible :', e);
        show(OFFER_CLASS);
    }
}

async function disable() {
    // Le serveur d'abord : couper l'affichage avant d'avoir supprimé le jeton
    // laisserait le parent croire qu'il ne reçoit plus rien alors que si.
    try {
        const token = await currentToken();

        if (token) {
            await sendToken('DELETE', meta('push-delete-url'), token);
        }

        const instance = await messaging();

        if (instance) {
            await instance.deleteToken();
        }
    } catch (e) {
        // Le jeton restera en base jusqu'à ce que Firebase le déclare mort.
        console.error('[push] Désactivation incomplète :', e);
    }

    write(ENABLED_KEY, false);
    show(OFFER_CLASS);
}

/**
 * Rejoue l'abonnement à chaque chargement de page. C'est ce qui répare tout
 * seul un jeton renouvelé par Firebase, et ce qui remet en service les autres
 * appareils du parent après une déconnexion — qui les efface tous côté serveur.
 */
async function resync() {
    try {
        const token = await currentToken();

        if (token) {
            await sendToken('POST', meta('push-store-url'), token);
        }
    } catch (e) {
        // Sans réseau, la prochaine page réessaiera.
        console.error('[push] Re-synchronisation impossible :', e);
    }
}

/**
 * Un téléphone familial est un appareil partagé : la déconnexion doit oublier
 * le jeton local, au même titre que les caches. Déclenché depuis la page de
 * login, ce qui couvre aussi l'expiration de session.
 */
async function forget() {
    if (!read(ENABLED_KEY)) {
        return;
    }

    write(ENABLED_KEY, false);

    try {
        const instance = await messaging();

        if (instance) {
            await instance.deleteToken();
        }
    } catch (e) {
        // Le serveur a de toute façon effacé ses jetons à la déconnexion.
    }
}

/**
 * Déplacement demandé par le service worker au clic sur une notification.
 *
 * C'est lui qui redonne le focus à l'onglet ; c'est à la page de se rendre sur
 * la destination, faute de pouvoir être navigée depuis un service worker qui
 * ne la contrôle pas.
 */
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
        const payload = event.data;

        if (!payload || payload.type !== 'PUSH_NOTIFICATION_CLICK' || !payload.url) {
            return;
        }

        window.location.assign(payload.url);
    });

    // Indispensable : le flux de messages n'est démarré implicitement que par
    // l'affectation de `onmessage`. Avec `addEventListener`, les messages
    // restent en file d'attente et n'arrivent jamais.
    if (typeof navigator.serviceWorker.startMessages === 'function') {
        navigator.serviceWorker.startMessages();
    }
}

document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
        return;
    }

    if (event.target.closest('[data-push-enable]')) {
        event.preventDefault();
        enable();

        return;
    }

    if (event.target.closest('[data-push-disable]')) {
        event.preventDefault();
        disable();

        return;
    }

    // Masquer n'est pas couper : l'encart descend en pied de page au
    // chargement suivant, il reste donc possible d'activer plus tard.
    if (event.target.closest('[data-push-close]')) {
        event.preventDefault();
        reportAppState('push_banner_dismissed');
        demote();
    }
});

function init() {
    if (!isSupported() || !isConfigured()) {
        return;
    }

    if (meta('pwa-purge-caches') === '1') {
        forget();

        return;
    }

    const wasEnabled = read(ENABLED_KEY);

    // Refus enregistré lors d'une visite précédente, éventuellement sur un
    // autre appareil : l'encart naît directement en bas.
    if (meta('push-dismissed') === '1') {
        demote();
    }

    // Permission retirée depuis les réglages du navigateur : on le dit, mais
    // seulement à qui les avait activées. Nul besoin de relancer celui qui a
    // refusé.
    if (Notification.permission === 'denied') {
        write(ENABLED_KEY, false);

        if (wasEnabled) {
            demote();
            show(BLOCKED_CLASS);
        }

        return;
    }

    // Déjà activées avant ce chargement : la question est tranchée, l'encart
    // n'a plus à occuper le haut de l'accueil.
    if (wasEnabled && Notification.permission === 'granted') {
        demote();
        show(ENABLED_CLASS);
        resync();

        return;
    }

    show(OFFER_CLASS);
}

init();
